import { AdMarker, AdMarkerPlacement } from '@/server/models/mongoose/helpers/types';

export interface SilencePeriod {
  start: number;
  end: number;
  duration: number;
}

export const MIN_MARKER_DISTANCE_PERCENTAGE = 0.15;
export const FIRST_MIDROLL_TARGET_PERCENTAGE = 33;
export const SECOND_MIDROLL_TARGET_PERCENTAGE = 67;
export const ONLY_MIDROLL_TARGET_PERCENTAGE = 50;
export const SEARCH_RANGE = MIN_MARKER_DISTANCE_PERCENTAGE * 100; // Equivalent to 15 if MIN_MARKER_DISTANCE_PERCENTAGE is 0.15

export function findLongestSilenceWithinRange(
  silencePeriods: SilencePeriod[],
  startPercentage: number,
  endPercentage: number,
  duration: number
): SilencePeriod | null {
  if (!silencePeriods?.length) {
    return null;
  }

  const startTime = (duration * startPercentage) / 100;
  const endTime = (duration * endPercentage) / 100;

  const validSilences = silencePeriods.filter(
    (silence) => silence.end >= startTime && silence.end <= endTime
  );

  if (!validSilences.length) {
    return null;
  }

  const longestSilence = validSilences.reduce(
    (longest, current) => (current.duration > longest.duration ? current : longest),
    validSilences[0]
  );

  return {
    start: longestSilence.end - longestSilence.duration,
    end: longestSilence.end,
    duration: longestSilence.duration,
  };
}

export function findOptimalMidrollPlacement(
  silencePeriods: SilencePeriod[],
  targetPercentage: number,
  episodeDuration: number,
  previousMarkerPosition: number
): number {
  const minGapTime = MIN_MARKER_DISTANCE_PERCENTAGE * episodeDuration;
  const earliestAllowedTimeAbsolute = previousMarkerPosition + minGapTime;
  const currentMidrollMinStartPercentage = (earliestAllowedTimeAbsolute / episodeDuration) * 100;
  const effectiveTargetPercentage = Math.max(targetPercentage, currentMidrollMinStartPercentage);

  const silenceSearchStartPercentage = Math.max(
    effectiveTargetPercentage - SEARCH_RANGE,
    currentMidrollMinStartPercentage
  );
  const silenceSearchEndPercentage = Math.min(effectiveTargetPercentage + SEARCH_RANGE, 100);

  const longestSilence = findLongestSilenceWithinRange(
    silencePeriods,
    silenceSearchStartPercentage,
    silenceSearchEndPercentage,
    episodeDuration
  );

  let finalPosition: number;

  if (!longestSilence) {
    finalPosition = (effectiveTargetPercentage / 100) * episodeDuration;
  } else {
    finalPosition = longestSilence.start + longestSilence.duration / 2;
  }

  return Math.min(Math.max(finalPosition, earliestAllowedTimeAbsolute), episodeDuration);
}

/**
 * Sort markers by placement and start time
 * @param markers - The markers to sort
 * 'preroll' should always be first
 * 'postroll' should always be last
 * midrolls should be sorted by start time
 * @returns The sorted markers
 */
export function sortMarkers(markers: AdMarker[]): AdMarker[] {
  return markers.sort((marker, nextMarker) => {
    const { placement, start } = marker;
    const { placement: nextPlacement, start: nextStart } = nextMarker;

    if (placement === AdMarkerPlacement.PREROLL || nextPlacement === AdMarkerPlacement.POSTROLL) {
      return -1;
    }
    if (placement === AdMarkerPlacement.POSTROLL || nextPlacement === AdMarkerPlacement.PREROLL) {
      return 1;
    }
    if (
      placement.includes(AdMarkerPlacement.MIDROLL) &&
      nextPlacement.includes(AdMarkerPlacement.MIDROLL) &&
      !!start &&
      !!nextStart
    ) {
      return start - nextStart;
    }

    return 0;
  });
}
